/**
 * @typedef ModalDefaults
 * @property {number} closeDuration
 * @property {boolean} closeOnOutsideClick
 * @property {boolean} closeOnEscape
 * @property {string} activeHTMLelementClass
 * @property {string} ariaLabel
 * @property {string} ariaLabelledBy
 * @property {string} ariaLabelClose
 * @property {string} closeButtonContent
 * @property {string} modalBackdropClass
 * @property {string} modalClass
 * @property {string} closeButtonClass
 * @property {string} contentContainerClass
 * @property {string} contentClass
 * @property {string} loadingContainerClass
 * @property {string} isLoadingClass
 * @property {Object} callbacks
 */

class Modal {
	/**
	 * Modal constructor
	 */
	constructor() {
		this.htmlEl = document.querySelector('html');
		this.backdropEl = null;
		this.modalEl = null;
		this.previousActiveElement = null;
		this.defaults = {
			closeDuration: 400,
			closeOnOutsideClick: true,
			closeOnEscape: true,
			activeHTMLelementClass: 'modal-active',
			ariaLabel: 'Dialog',
			ariaLabelledBy: '',
			ariaLabelClose: 'Close modal',
			closeButtonContent: '&times;',
			modalBackdropClass: 'modal-backdrop',
			modalClass: 'modal',
			closeButtonClass: 'modal__close',
			contentContainerClass: 'modal__content-container',
			contentClass: 'modal__content',
			loadingContainerClass: 'modal__loading-container',
			isLoadingClass: 'is-loading',
			callbacks: {}
		};
		this.currentOpts = { ...this.defaults };
	}

	/**
	 * Set the defaults used by all modals
	 *
	 * @param {ModalDefaults} defaults A {@link ModalDefaults} object
	 */
	setDefaults(defaults = {}) {
		this.defaults = {
			...this.defaults,
			...defaults
		};
	}

	/**
	 * Retrieve a modal setting or use a default if it does not exist
	 *
	 * @param {string} settingName The setting name
	 * @param {any} defaultValue The default value
	 * @returns {any}
	 */
	getSetting(settingName, defaultValue = '') {
		return this.currentOpts[settingName] ?? this.defaults[settingName] ?? defaultValue;
	}

	/**
	 * Focusable elements within a container (for focus trapping).
	 *
	 * @param {HTMLElement|null} container
	 * @returns {HTMLElement[]}
	 */
	getFocusableElements(container) {
		if (!container) {
			return [];
		}

		return Array.from(
			container.querySelectorAll(
				'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
			)
		).filter((el) => {
			if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') {
				return false;
			}

			const style = window.getComputedStyle(el);

			return style.display !== 'none' && style.visibility !== 'hidden';
		});
	}

	/**
	 * Initialize the modal
	 */
	initModal() {
		return new Promise((resolve) => {
			this.modalEl = this.createModalEl();
			this.backdropEl = this.createBackdropEl(this.modalEl);

			document.body.appendChild(this.backdropEl);

			const closeEl = this.modalEl.querySelector(`.${this.getSetting('closeButtonClass')}`);

			closeEl.addEventListener('click', (e) => {
				e.preventDefault();

				this.hide();
			});

			setTimeout(() => {
				resolve();
			}, 0);
		});
	}

	/**
	 * Create the modal backdrop element and append an optional child
	 *
	 * @param {HTMLElement|null} modalEl Optional modal element child to append to the backdrop element
	 * @returns {HTMLDivElement} The backdrop element
	 */
	createBackdropEl(modalEl = null) {
		const backdropEl = document.createElement('div');

		backdropEl.setAttribute('class', this.getSetting('modalBackdropClass'));

		if (modalEl) {
			backdropEl.appendChild(modalEl);
		}

		return backdropEl;
	}

	/**
	 * Create and return the modal element
	 *
	 * @returns {HTMLDivElement} The modal element
	 */
	createModalEl() {
		const modalEl = document.createElement('div');

		modalEl.setAttribute('class', this.getSetting('modalClass'));
		modalEl.setAttribute('role', 'dialog');
		modalEl.setAttribute('aria-modal', 'true');
		modalEl.setAttribute('tabindex', '-1');

		const labelledBy = this.getSetting('ariaLabelledBy');

		if (labelledBy) {
			modalEl.setAttribute('aria-labelledby', labelledBy);
		} else {
			modalEl.setAttribute('aria-label', this.getSetting('ariaLabel', 'Dialog'));
		}

		modalEl.innerHTML = `
			<button
				type="button"
				class="${this.getSetting('closeButtonClass')}"
				aria-label="${this.getSetting('ariaLabelClose')}"
				data-modal-close
			>
				${this.getSetting('closeButtonContent')}
			</button>
			<div class="${this.getSetting('contentContainerClass')}">
				<div class="${this.getSetting('loadingContainerClass')}">
					<div class="ui__spin-loader"></div>
				</div>
				<div class="${this.getSetting('contentClass')}"></div>
			</div>
		`.trim();

		return modalEl;
	}

	/**
	 * Set dialog accessible name from opts and/or content (WCAG 4.1.2).
	 * Preference: ariaLabelledBy opt → heading in content → img[alt] → ariaLabel opt/default.
	 */
	updateAccessibleName() {
		if (!this.modalEl) {
			return;
		}

		const labelledBy = this.getSetting('ariaLabelledBy');

		if (labelledBy) {
			this.modalEl.setAttribute('aria-labelledby', labelledBy);
			this.modalEl.removeAttribute('aria-label');
			return;
		}

		const contentEl = this.modalEl.querySelector(`.${this.getSetting('contentClass')}`);
		const heading = contentEl?.querySelector('h1, h2, h3, h4, h5, h6');

		if (heading) {
			if (!heading.id) {
				heading.id = 'modal-dialog-title';
			}

			this.modalEl.setAttribute('aria-labelledby', heading.id);
			this.modalEl.removeAttribute('aria-label');
			return;
		}

		const img = contentEl?.querySelector('img[alt]');

		if (img?.getAttribute('alt')) {
			this.modalEl.setAttribute('aria-label', img.getAttribute('alt'));
			this.modalEl.removeAttribute('aria-labelledby');
			return;
		}

		this.modalEl.setAttribute('aria-label', this.getSetting('ariaLabel', 'Dialog'));
		this.modalEl.removeAttribute('aria-labelledby');
	}

	/**
	 * Handler for outside click events
	 *
	 * @param {Event} e
	 */
	clickOutsideContentListener = (e) => {
		if (
			this.getSetting('closeOnOutsideClick', true) &&
			e.target.classList.contains(this.getSetting('modalBackdropClass'))
		) {
			this.hide();
		}
	};

	/**
	 * Handler for an escape key press
	 *
	 * @param {Event} e
	 */
	escapeListener = (e) => {
		if (this.getSetting('closeOnEscape', true) && (e.key === 'Escape' || e.keyCode === 27)) {
			this.hide();
		}
	};

	/**
	 * Keep Tab / Shift+Tab cycling inside the open modal.
	 *
	 * @param {KeyboardEvent} e
	 */
	trapFocusListener = (e) => {
		if (e.key !== 'Tab' || !this.modalEl) {
			return;
		}

		const focusables = this.getFocusableElements(this.modalEl);

		if (!focusables.length) {
			e.preventDefault();
			this.modalEl.focus();
			return;
		}

		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		const active = document.activeElement;

		if (e.shiftKey) {
			if (active === first || !this.modalEl.contains(active)) {
				e.preventDefault();
				last.focus();
			}
		} else if (active === last || !this.modalEl.contains(active)) {
			e.preventDefault();
			first.focus();
		}
	};

	/**
	 * Move initial focus into the modal (close button when available).
	 */
	focusInitialElement() {
		if (!this.modalEl) {
			return;
		}

		const focusables = this.getFocusableElements(this.modalEl);
		const closeEl = this.modalEl.querySelector(`.${this.getSetting('closeButtonClass')}`);

		(closeEl || focusables[0] || this.modalEl).focus();
	}

	/**
	 * Display the modal
	 *
	 * @param {string} content The modal content markup
	 * @param {ModalDefaults} opts An optional {@link ModalDefaults} object
	 */
	async show(content = '', opts = {}) {
		this.currentOpts = {
			...this.defaults,
			...opts
		};

		if (this.modalEl !== null) {
			console.error('Modal.show() cannot be run since another modal is already active');
		}

		this.previousActiveElement = document.activeElement instanceof HTMLElement
			? document.activeElement
			: null;

		await this.initModal();

		this.htmlEl.classList.add(this.getSetting('activeHTMLelementClass'));

		if (content) {
			this.setContent(content);
		} else {
			this.setLoading(true);
		}

		this.backdropEl.addEventListener('click', this.clickOutsideContentListener);
		window.addEventListener('keydown', this.escapeListener);
		window.addEventListener('keydown', this.trapFocusListener);

		requestAnimationFrame(() => {
			this.focusInitialElement();
		});
	}

	/**
	 * Hide the modal
	 */
	hide() {
		this.htmlEl.classList.remove(this.getSetting('activeHTMLelementClass'));

		if (
			this.currentOpts.callbacks?.onHide &&
			typeof this.currentOpts.callbacks.onHide === 'function'
		) {
			this.currentOpts.callbacks.onHide();
		}

		this.backdropEl.removeEventListener('click', this.clickOutsideContentListener);
		window.removeEventListener('keydown', this.escapeListener);
		window.removeEventListener('keydown', this.trapFocusListener);

		const returnFocusTo = this.previousActiveElement;
		this.previousActiveElement = null;

		setTimeout(() => {
			this.modalEl.remove();
			this.modalEl = null;
			this.backdropEl.remove();
			this.backdropEl = null;

			if (returnFocusTo && typeof returnFocusTo.focus === 'function') {
				returnFocusTo.focus();
			}
		}, this.currentOpts.closeDuration || 400);
	}

	/**
	 * Set the modal loading status
	 *
	 * @param {boolean} isLoading
	 */
	setLoading(isLoading) {
		if (isLoading) {
			this.modalEl.classList.add(this.getSetting('isLoadingClass'));
		} else {
			this.modalEl.classList.remove(this.getSetting('isLoadingClass'));
		}
	}

	/**
	 * Set the modal content markup and optionally remove the loading state
	 *
	 * @param {string} content The modal content markup
	 * @param {boolean} removeLoader Flag to remove the loading state
	 */
	setContent(content = '', removeLoader = true) {
		const contentEl = this.modalEl.querySelector(`.${this.getSetting('contentClass')}`);

		contentEl.innerHTML = content;

		if (removeLoader) {
			this.setLoading(false);
		}

		this.updateAccessibleName();

		// Content may introduce new focusable nodes after open.
		requestAnimationFrame(() => {
			if (this.modalEl && !this.modalEl.contains(document.activeElement)) {
				this.focusInitialElement();
			}
		});
	}
}

export default new Modal();
